// External parsers which handle standard translating to and from Options values
// Might be worth considering a SearchspringOption class

import { SearchspringFetcher } from "@app/lib/searchspring";
import { Options, useSearchspringContext } from "@app/providers/searchspring";

function stringifyOption(options: Options, key: string) {
  const value = options.get(key);
  return !value
    ? ""
    : value instanceof Set
      ? Array.from(value).join(",")
      : typeof value === "number"
        ? value.toString()
        : value;
}

function parseOption(params: URLSearchParams, key: string) {
  const value = params.get(key);
  if (!value) return "";

  switch (key) {
    case "filters":
      return new Set(value.split(","));
    case "page":
      return parseInt(value);
    default:
      return value;
  }
}

function setParamsToURL(params: URLSearchParams) {
  const url = new URL(window.location.href);
  url.search = params.toString();
  window.history.replaceState({}, "", url);
}

function getParamsFromURL() {
  return new URLSearchParams(window.location.search);
}

export function useSearchspringOptionsSync<Client extends SearchspringFetcher<Response>, Response>() {
  const { refine, options, setOptions } = useSearchspringContext<Client, Response>();

  function setOptionToURL(key: string) {
    const params = getParamsFromURL();
    const value = stringifyOption(options, key);
    if (value) params.set(key, value.toString());
    else params.delete(key);
    setParamsToURL(params);
  }

  function getOptionFromURL(key: string) {
    const params = getParamsFromURL();
    const value = parseOption(params, key);
    if (value) refine(key, value);
  }

  function setAllOptionsToURL() {
    const params = new URLSearchParams();
    Object.keys(options).forEach((key) => {
      params.set(key, stringifyOption(options, key));
    });
    setParamsToURL(params);
  }

  function getAllOptionsFromURL() {
    const params = getParamsFromURL();

    setOptions((prev) => {
      const next = new Map(prev);
      Object.keys(params).forEach((key) => {
        next.set(key, parseOption(params, key));
      });
      return next;
    });
  }

  return {
    getOptionFromURL,
    setOptionToURL,
    getAllOptionsFromURL,
    setAllOptionsToURL,
  };
}
